import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles, Tags } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { LoadingButton } from '../../common/LoadingButton';
import {
  FACET_ORDER,
  SINGLE_VALUE_FACETS,
  TAXONOMY_CONFIDENCE_THRESHOLD,
} from '@constants/documentMetadata';
import type { TaxonomyReviewState } from '@hooks/useTaxonomyReview';
import type {
  Facet, ReasonCode, SuggestionErrorCode, SuggestionRunStatus, TaxonomyAssignment,
} from '@/types/taxonomy';

interface TaxonomyReviewPanelProps {
  review: TaxonomyReviewState;
}

const closed = <T extends string>(rows: Record<T, string>): Readonly<Record<T, string>> =>
  Object.assign(Object.create(null) as Record<T, string>, rows);

// Null-prototype: the key comes from a response body, and a plain literal would
// answer 'constructor' / 'toString' off Object.prototype.
const RUN_STATUS_KEY = closed<SuggestionRunStatus>({
  not_attempted: 'notAttempted',
  pending: 'pending',
  running: 'running',
  succeeded: 'succeeded',
  failed: 'failed',
});

const FACET_KEY = closed<Facet>({
  source_scope: 'source_scope',
  domain: 'domain',
  document_type: 'document_type',
  authority: 'authority',
  product: 'product',
  project: 'project',
  customer: 'customer',
});

/**
 * Only a code this build has copy for reaches a translation key. Declared as a
 * Record over the union, so a new `SuggestionErrorCode` without a row here is a
 * compile error rather than a raw key on screen.
 */
const RUN_ERROR_CODES: Record<SuggestionErrorCode, true> = {
  provider_timeout: true,
  provider_error: true,
  invalid_output: true,
  no_active_taxonomy: true,
  extraction_unavailable: true,
  extraction_failed: true,
  no_extractable_text: true,
  internal_error: true,
};

export const RUN_ERROR_KEYS = new Set<string>(Object.keys(RUN_ERROR_CODES));

/** The domain closed `ReasonCode` so the UI could localise it; this is that. */
const REASON_CODES: Record<ReasonCode, true> = {
  filename_signal: true,
  heading_signal: true,
  body_signal: true,
  category_mapping: true,
  default_fallback: true,
};

export const REASON_KEYS = new Set<string>(Object.keys(REASON_CODES));

const rank = (facet: string): number => {
  const at = FACET_ORDER.indexOf(facet as Facet);
  return at === -1 ? FACET_ORDER.length : at;
};

/** Groups from the DATA, so a facet this build does not know still renders. */
const byFacet = (rows: TaxonomyAssignment[]): Array<[string, TaxonomyAssignment[]]> => {
  const groups = new Map<string, TaxonomyAssignment[]>();
  rows.forEach((row) => groups.set(row.facet, [...(groups.get(row.facet) ?? []), row]));
  return [...groups.entries()].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
};

const TaxonomyReviewPanel: React.FC<TaxonomyReviewPanelProps> = ({ review }) => {
  const { t, i18n } = useTranslation();
  const { taxonomy } = review;

  // The tenant has the feature off, or the document is not ours (404 for both on
  // purpose). Nothing before the first answer either, or a flag-off tenant would
  // see the section appear and vanish.
  if (review.unavailable || (!taxonomy && !review.error)) return null;

  const label = (row: TaxonomyAssignment): string => {
    const name = i18n.language.startsWith('ja') ? row.displayNameJa : row.displayNameEn;
    return name || row.code || row.termId;
  };

  const facetLabel = (facet: string): string => (
    FACET_KEY[facet as Facet]
      ? t(`metadata.taxonomy.facet.${FACET_KEY[facet as Facet]}`)
      : facet
  );

  const renderRow = (row: TaxonomyAssignment, offer: 'both' | 'reject') => {
    const inFlight = review.busy[row.termId];
    const low = row.confidence !== null && row.confidence < TAXONOMY_CONFIDENCE_THRESHOLD;
    const deprecated = row.termStatus === 'deprecated';
    // No resolvable code means no change can name this term, so neither button
    // would do anything. Otherwise the two verbs part ways: the API refuses a
    // NEW assignment to a deprecated term but keeps it rejectable, and a decided
    // value can only be rejected — which is how a single-valued facet is freed.
    const canReject = !!row.code;
    const canConfirm = canReject && offer === 'both' && !deprecated;
    return (
      <li key={row.termId} className="flex flex-wrap items-center gap-2 py-1">
        <span className="revy-cjk max-w-xs truncate text-sm leading-[1.6]" title={label(row)}>
          {label(row)}
        </span>

        {row.source === 'ai' && row.confidence !== null && (
          <Badge variant={low ? 'outline' : 'secondary'} className="text-xs">
            {t('metadata.taxonomy.confidence', {
              percent: Math.round(row.confidence * 100),
            })}
          </Badge>
        )}
        {low && (
          <span className="revy-cjk text-xs text-muted-foreground">
            {t('metadata.taxonomy.lowConfidence')}
          </span>
        )}
        {deprecated && (
          <span className="revy-cjk flex items-center gap-1 text-xs text-muted-foreground">
            <AlertTriangle className="size-3.5" strokeWidth={1.9} />
            {t('metadata.taxonomy.deprecatedTerm')}
          </span>
        )}
        {!row.code && (
          <span className="revy-cjk text-xs text-muted-foreground">
            {t('metadata.taxonomy.unresolvableTerm')}
          </span>
        )}
        {row.source === 'ai' && row.reasonCode && REASON_KEYS.has(row.reasonCode) && (
          <span className="revy-cjk text-xs text-muted-foreground">
            {t(`metadata.taxonomy.reason.${row.reasonCode}`)}
          </span>
        )}
        {row.source === 'ai' && row.generatorVersion && (
          <span className="text-xs text-muted-foreground">
            {t('metadata.taxonomy.aiProvenance', { version: row.generatorVersion })}
          </span>
        )}

        {canReject && (
          <span className="ml-auto flex gap-2">
            {canConfirm && (
              <LoadingButton
                size="sm"
                // Every row's buttons would otherwise share one accessible
                // name, so assistive tech could not tell which term they act
                // on — and the name carries the STATE too, because a static
                // label masks the loading text the visible button swaps in.
                aria-label={t(inFlight === 'confirm'
                  ? 'metadata.taxonomy.action.confirmingTerm'
                  : 'metadata.taxonomy.action.confirmTerm', { name: label(row) })}
                disabled={inFlight === 'reject'}
                loading={inFlight === 'confirm'}
                onClick={() => void review.confirm(row)}
                loadingText={t('metadata.taxonomy.action.confirming')}
              >
                {t('metadata.taxonomy.action.confirm')}
              </LoadingButton>
            )}
            <LoadingButton
              size="sm"
              variant="outline"
              aria-label={t(inFlight === 'reject'
                ? 'metadata.taxonomy.action.rejectingTerm'
                : 'metadata.taxonomy.action.rejectTerm', { name: label(row) })}
              disabled={inFlight === 'confirm'}
              loading={inFlight === 'reject'}
              onClick={() => void review.reject(row)}
              loadingText={t('metadata.taxonomy.action.rejecting')}
            >
              {t('metadata.taxonomy.action.reject')}
            </LoadingButton>
          </span>
        )}
      </li>
    );
  };

  const decidedFacets = new Set(
    [...(taxonomy?.confirmed ?? []), ...(taxonomy?.migrated ?? [])].map((row) => row.facet),
  );

  const section = (
    titleKey: string, rows: TaxonomyAssignment[], offer: 'both' | 'reject',
  ) => {
    if (rows.length === 0) return null;
    return (
      <div className="mb-4">
        <h4 className="revy-cjk mb-1 text-sm font-medium leading-[1.5]">{t(titleKey)}</h4>
        {byFacet(rows).map(([facet, group]) => (
          <div key={facet} className="mb-2">
            <p className="revy-cjk text-xs text-muted-foreground">{facetLabel(facet)}</p>
            <ul>{group.map((row) => renderRow(row, offer))}</ul>
            {/* Only where confirming is what the API refuses: the facet already
                holds a decided value, so a rival needs the reject first. The
                decided row above carries that Reject button. */}
            {offer === 'both'
              && SINGLE_VALUE_FACETS.includes(facet as Facet)
              && decidedFacets.has(facet as Facet) && (
              <p className="revy-cjk mt-1 text-xs text-muted-foreground">
                {t('metadata.taxonomy.singleValueHint')}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const run = taxonomy?.suggestionRun;
  const runMessage = (): string => {
    if (!run) return '';
    if (run.status === 'failed') {
      if (!run.retryable) return t('metadata.taxonomy.suggestionRun.failedNotRetryable');
      const key = run.errorCode && RUN_ERROR_KEYS.has(run.errorCode) ? run.errorCode : null;
      if (key) return t(`metadata.taxonomy.suggestionError.${key}`);
    }
    if (run.status === 'succeeded' && run.candidateCount === 0) {
      return t('metadata.taxonomy.suggestionRun.succeededNoCandidates');
    }
    return t(`metadata.taxonomy.suggestionRun.${RUN_STATUS_KEY[run.status] ?? 'notAttempted'}`);
  };

  return (
    <section className="mt-8 rounded-lg border p-4">
      <h3 className="revy-cjk mb-2 flex items-center gap-2 text-base font-medium leading-[1.5]">
        <Tags className="size-5 text-primary" strokeWidth={1.9} />
        {t('metadata.taxonomy.panelTitle')}
      </h3>
      <p className="revy-cjk mb-4 text-sm leading-[1.6] text-muted-foreground">
        {t('metadata.taxonomy.panelDescription')}
      </p>

      {/* Rendered from first paint, text-only conditional: a polite region
          inserted together with its text is the case screen readers are least
          reliable about announcing. */}
      <p
        role="alert"
        className={review.error ? 'revy-cjk mb-3 text-sm text-destructive' : 'sr-only'}
      >
        {review.error}
      </p>
      <p
        role="status"
        className={review.notice ? 'revy-cjk mb-3 text-sm text-muted-foreground' : 'sr-only'}
      >
        {review.notice}
      </p>

      {taxonomy && (
        <>
          {/* Reject only on the decided sections: the API refuses add/confirm on
              a migrated row, and rejecting is how a single-valued facet is freed. */}
          {section('metadata.taxonomy.section.confirmed', taxonomy.confirmed, 'reject')}
          {section('metadata.taxonomy.section.migrated', taxonomy.migrated, 'reject')}
          {section('metadata.taxonomy.section.suggested', taxonomy.suggested, 'both')}

          {taxonomy.confirmed.length === 0
            && taxonomy.migrated.length === 0
            && taxonomy.suggested.length === 0 && (
            <p className="revy-cjk mb-4 text-sm text-muted-foreground">
              {t('metadata.taxonomy.empty')}
            </p>
          )}

          {run && (
            <div className="flex flex-wrap items-center gap-3 border-t pt-3">
              <Sparkles className="size-4 text-primary" strokeWidth={1.9} />
              <span className="revy-cjk text-sm leading-[1.6]">{runMessage()}</span>
              {run.attemptCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('metadata.taxonomy.suggestionRun.attemptCount', {
                    count: run.attemptCount,
                  })}
                </span>
              )}
              {run.candidateCount !== null && run.candidateCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('metadata.taxonomy.suggestionRun.candidateCount', {
                    count: run.candidateCount,
                  })}
                </span>
              )}
              {/* The RUN's answer. A viewer sees it and still gets 403 on press;
                  the server is the authority, not this flag. */}
              {run.retryable && (
                <LoadingButton
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  loading={review.retrying}
                  onClick={() => void review.retry()}
                  loadingText={t('metadata.taxonomy.retry.retrying')}
                >
                  {t('metadata.taxonomy.retry.action')}
                </LoadingButton>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default TaxonomyReviewPanel;
